import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  MenuItem,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { ApiServices } from '../../utils/ApiService';

import OfficeForm from './forms/OfficeForm';
import RegistrationForm from './forms/RegistrationForm';
import PaymentForm from './forms/PaymentForm';
import EcCcForm from './forms/EcCcForm';
import ChequeHandoverForm from './forms/ChequeHandoverForm';

import { useSnackbar } from 'notistack';

const ExpenseDynamicPage = () => {
    const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const isEdit = !!id;

  const [category, setCategory] = useState('');
  const [formData, setFormData] = useState({});
  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [expenseMeta, setExpenseMeta] = useState({});
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // 🔥 LOAD EDIT DATA
  useEffect(() => {
    if (id) fetchExpense();
  }, [id]);

  const fetchExpense = async () => {
    const res = await ApiServices.get(`expenses/${id}`);
    const data = res.data;

    setCategory(data.category);

    setExpenseMeta({
      emp_id: Number(data.emp_id),
      is_approved: data.is_approved,
      status: data.status
    });

    if (data.category === 'REGISTRATION' || data.category === 'PAYMENT') {
      setInitialData(mapRegistrationData(data));
    } else if (data.category === 'EC/CC') {
      setInitialData(data.expense_json);
    } else if (data.category === 'PAYMENT') {
      setInitialData(data.expense_json);
    } else if (data.category === 'CHEQUEHANDOVER') {
      setInitialData({
        meta:
          data.expense_json?.meta || {},

        cheque_handover_status:
          data.expense_json
            ?.cheque_handover_status || 'PENDING',

        customer:
          data.expense_json?.customer || {
            name: '',
            mobile: '',
            reference_staff: '',
            cheque_type: 'BT',
            from_bank: '',
            to_bank_sro: ''
          },

        acknowledgement:
          data.expense_json?.acknowledgement || {
            handover_to: '',
            handover_by: ''
          },

        document_details:
          data.expense_json?.document_details || [],

        expenses:
          data.expense_json?.expenses || []

      });
    } else {
      setInitialData({
        meta: data.expense_json?.meta || {
          employee_name: data.emp_name,
          emp_id: Number(data.emp_id),
          date: new Date(data.created_at).toLocaleDateString()
        },
        rows: data.expense_json?.rows || []
      });
    }
  };

  // 🔥 REGISTRATION MAPPER
  const expenseRows = [
    'STAMP','REGISTRATION','CD','COMPUTER','LISION',
    'REFERENCE','FIELD INSP AMOUNT','PETROL','FOOD',
    'STATIONARY','EC'
  ];

  const mapRegistrationData = (data) => {
    const json = data.expense_json || {};

    return {
      meta: json.meta || {
        employee_name: data.emp_name,
        emp_id: Number(data.emp_id),
        date: new Date(data.created_at).toLocaleDateString()
      },

      customer: {
        name: json.customer?.name || '',
        mobile: json.customer?.mobile || '',
        reference: json.customer?.reference || '',
        received_amount: json.customer?.received_amount || 0,
        doc_type: json.customer?.doc_type || 'SALE',
        bank: json.customer?.bank || '',
        sro: json.customer?.sro || '',
        advocate_fees: json.customer?.advocate_fees || 0
      },

      rows: expenseRows.map((label, i) => {
        const row = json.rows?.[i] || {};
        return {
          label,
          amount: row.amount || 0,
          payment_status: row.payment_status || 'PENDING',
          paid_status: row.paid_status || 'NA'
        };
      }),

      registration_status: json.registration_status || 'PENDING'
    };
  };

  // 🔥 ROLE FLAGS
  const isOwner = user?.id === expenseMeta.emp_id;
  const isSuperAdmin = user?.role === 'Super Admin';
  const isApproved = expenseMeta.is_approved || expenseMeta.status === 'APPROVED';
  const isRejected = expenseMeta.status?.includes('REJECTED');

  // 🔥 RENDER FORM
  const renderForm = () => {
    const props = {
      onChange: setFormData,
      initialData,
      user
    };

    switch (category) {
      case 'OFFICE':
        return <OfficeForm {...props} />;
      case 'REGISTRATION':
        return <RegistrationForm {...props} />;
      case 'PAYMENT':
        return <PaymentForm {...props} />;
      case 'EC/CC':
        return <EcCcForm {...props} />;
      case 'CHEQUEHANDOVER':
        return <ChequeHandoverForm {...props} />;
      default:
        return <Typography color="gray">Select category</Typography>;
    }
  };

  // 🔥 SUBMIT
  const handleSubmit = async () => {
    if (!category) return enqueueSnackbar('Select category', { variant: 'warning' });

    setLoading(true);

    try {
      let expense_json = {};

      if (category === 'OFFICE') {
        expense_json = {
          meta: formData.meta,
          rows: formData.rows.map(r => ({
            sub_category: r.sub_category || r.label,
            description: r.description || '',
            amount: Number(r.amount || 0)
          }))
        };
      }

      else if (category === 'REGISTRATION' || category === 'PAYMENT') {
        expense_json = {
          meta: formData.meta,
          customer: {
            name: formData.customer.name,
            mobile: formData.customer.mobile,
            reference: formData.customer.reference,
            received_amount: Number(formData.customer.received_amount || 0),
            doc_type: formData.customer.doc_type,
            bank: formData.customer.bank,
            sro: formData.customer.sro,
            advocate_fees: Number(formData.customer.advocate_fees || 0)
          },
          rows: formData.rows.map(r => ({
            label: r.label,
            amount: Number(r.amount || 0),
            payment_status: r.payment_status,
            paid_status: r.paid_status
          })),
          registration_status: formData.registration_status
        };
      }

      else if (category === 'EC/CC') {

        expense_json = {
          meta: formData.meta,

          sub_category: formData.sub_category,

          ec: formData.ec,

          cc: formData.cc
        };
      }

      else if (category === 'CHEQUEHANDOVER') {

        expense_json = {

          meta: formData.meta,

          cheque_handover_status:
            formData.cheque_handover_status,

          customer: formData.customer,

          acknowledgement:
            formData.acknowledgement,

          document_details:
            formData.document_details || [],

          expenses:
            formData.expenses || [],

          total:
            formData.total || 0
        };
      }

      const payload = {
        emp_id: user.id,
        category,
        expense_json
      };

      if (isEdit) {
        await ApiServices.post(`expenses/update/${id}`, payload);
      } else {
        await ApiServices.post(`expenses/create`, payload);
      }

      enqueueSnackbar('Saved successfully', { variant: 'success' });
    } catch (err) {
      console.error(err);
      enqueueSnackbar('Error while saving', { variant: 'error' });
    }

    setLoading(false);
  };

  // 🔥 APPROVE
  const handleApprove = async () => {
    try {
      await ApiServices.post(`expenses/approve/${id}`, {
        user_id: user.id,
        action: 'APPROVED',
        notes: ''
      });

      enqueueSnackbar('Approved successfully', { variant: 'success' });
      window.location.reload();
    } catch (err) {
      enqueueSnackbar('Approval failed', { variant: 'error' });
    }
  };

  // 🔥 REJECT
  const handleReject = async () => {
    if (!rejectReason) return 
      enqueueSnackbar('Enter reject reason', { variant: 'info' });

    try {
      await ApiServices.post(`expenses/approve/${id}`, {
        user_id: user.id,
        action: 'REJECTED',
        notes: rejectReason
      });

      enqueueSnackbar('Rejected successfully', { variant: 'success' });
      setRejectOpen(false);
      window.location.reload();

    } catch (err) {
      enqueueSnackbar('Reject failed', { variant: 'error' });
    }
  };

  return (
    <Box p={3}>
      <Paper sx={{ p: 4, maxWidth: 1000, margin: 'auto' }}>

        {/* 🔥 STATUS */}
        <Box textAlign="center" mb={2}>
          {isApproved && (
            <Typography sx={{ color: 'green', fontWeight: 600 }}>
              ✅ Approved
            </Typography>
          )}
          {isRejected && (
            <Typography sx={{ color: 'red', fontWeight: 600 }}>
              ❌ Rejected
            </Typography>
          )}
        </Box>

        {/* CATEGORY */}
        <TextField
          select
          label="Category"
          fullWidth
          value={category}
          disabled={isEdit}
          onChange={(e) => setCategory(e.target.value)}
          sx={{ mb: 3 }}
        >
          <MenuItem value="REGISTRATION">Registration</MenuItem>
          <MenuItem value="OFFICE">Office</MenuItem>
          <MenuItem value="CHEQUEHANDOVER">Cheque Handover</MenuItem>
          <MenuItem value="PAYMENT">Payment</MenuItem>
          <MenuItem value="EC/CC">EC/CC</MenuItem>
        </TextField>

        {/* FORM */}
        {renderForm()}

        {/* BUTTONS */}
        {category && (
          <Box mt={4} display="flex" justifyContent="center" gap={2}>

            {!isEdit && (
              <Button variant="contained" onClick={handleSubmit}>
                Submit
              </Button>
            )}

            {isEdit && isOwner && !isApproved && !isRejected && (
              <Button variant="contained" onClick={handleSubmit}>
                Update
              </Button>
            )}

            {isEdit && isSuperAdmin && !isApproved && !isRejected && (
              <>
                <Button color="success" variant="contained" onClick={handleApprove}>
                  Approve
                </Button>
                <Button color="error" variant="contained" onClick={() => setRejectOpen(true)}>
                  Reject
                </Button>
              </>
            )}

          </Box>
        )}

        {/* REJECT DIALOG */}
        <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} fullWidth>
          <DialogTitle>Reject Expense</DialogTitle>

          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Enter rejection reason..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button color="error" onClick={handleReject}>Reject</Button>
          </DialogActions>
        </Dialog>

      </Paper>
    </Box>
  );
};

export default ExpenseDynamicPage;